import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { BiometriaDto, MeuCertificadoDto, ImportarMeuCertificadoDto, ListarCertificadosVencendoDto } from './smovault.dto';
import { UrlService } from '../servicos/url.service';

@Injectable({
  providedIn: 'root'
})
export class SMOVaultService {
  private readonly bioUrl = `${this.url.url('biometrias/por-numero-inscricao', 'vault')}`;
  private readonly certUrl = `${this.url.url('meus/certificados', 'vault')}`;
  
  // Subject para notificar quando um certificado for importado ou anulado
  private certificadoAtualizadoSubject = new Subject<void>();
  public certificadoAtualizado$ = this.certificadoAtualizadoSubject.asObservable();

  constructor(private http: HttpClient, private url: UrlService) { }

  /**
   * Obtém uma biometria pelo número de inscrição.
   * @param numeroInscricao Número de inscrição da biometria.
   * @returns Observable contendo o objeto BiometriaDto.
   */
  obterPorNumeroInscricao(numeroInscricao: string): Observable<BiometriaDto> {
    return this.http.get<BiometriaDto>(`${this.bioUrl}/${numeroInscricao}`);
  }

  /**
   * Lista todos os certificados não anulados do usuário autenticado.
   * @returns Observable contendo a lista de certificados não anulados.
   */
  listarMeusCertificados(): Observable<MeuCertificadoDto[]> {
    return this.http.get<MeuCertificadoDto[]>(this.certUrl);
  }

  /**
   * Importa um novo certificado digital para o usuário autenticado.
   * Anula automaticamente o certificado anterior ativo.
   * @param dados Dados do certificado (arquivo base64 e senha).
   * @returns Observable contendo os dados do certificado importado.
   */
  importarMeuCertificado(dados: ImportarMeuCertificadoDto): Observable<MeuCertificadoDto> {
    return this.http.post<MeuCertificadoDto>(this.certUrl, dados);
  }

  /**
   * Lista certificados do usuário que vencem dentro do período especificado ou que já venceram.
   * Não inclui certificados anulados.
   * @param periodo Período de consulta (dataInicial e dataFinal).
   * @returns Observable contendo a lista de certificados vencendo ou vencidos.
   */
  listarMeusCertificadosVencendoOuVencidos(periodo: ListarCertificadosVencendoDto): Observable<MeuCertificadoDto[]> {
    const params = new HttpParams()
      .set('dataInicial', periodo.dataInicial.toISOString())
      .set('dataFinal', periodo.dataFinal.toISOString());

    return this.http.get<MeuCertificadoDto[]>(`${this.certUrl}/vencidos-vencendo`, { params });
  }

  /**
   * Anula um certificado do usuário autenticado.
   * @param certificadoId ID do certificado a ser anulado.
   * @param motivo Motivo da anulação (opcional).
   * @returns Observable contendo os dados do certificado anulado.
   */
  anularMeuCertificado(certificadoId: number, motivo?: string): Observable<MeuCertificadoDto> {
    let params = new HttpParams().set('motivo', motivo?.toString() || 'Anulado pelo usuário.');
    return this.http.delete<MeuCertificadoDto>(`${this.certUrl}/${certificadoId}`, { params });
  }

  /**
   * Notifica que um certificado foi atualizado (importado ou anulado).
   * Usado para disparar reavaliação de certificados vencidos/vencendo.
   */
  notificarCertificadoAtualizado(): void {
    this.certificadoAtualizadoSubject.next();
  }
}
